import type { PayloadAction } from '@reduxjs/toolkit';
import { contentApiLoadError } from '@store/alerts/alertConstants';
import { setAlert } from '@store/alerts/alertsState';
import type { RootState } from '@store/storeSetup';
import { selectCurriculumGradeForIntroduction } from '@store/curriculum/curriculumSelectors';
import { setUrlParams } from '@store/curriculum/curriculumState';
import { logAndCaptureException } from '@utils/logAndCaptureException';
import { all, call, fork, put, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';
import {
  initCurriculaGoalLists,
  initCurriculaGoalListsFailed,
  initCurriculaRelations,
  initCurriculaRelationsFailed,
  initCurriculaRoots,
  initCurriculaRootsFailed,
  loadCurriculum,
  loadCurriculumFailed,
  loadDocument,
  loadDocumentFailed,
  loadGoalFailed,
  loadGoals,
  loadGoalsCorrelations,
  loadGoalsCorrelationsFailed,
  setCurriculaGoalLists,
  setCurriculaRelations,
  setCurriculaRoots,
  setCurriculum,
  setDocument,
  setGoal,
  setGoalsCorrelations,
} from './contentApiState';
import {
  CONTENTAPI_DOCUMENT_KEYS,
  getCurriculaGoalLists,
  getCurriculaRelations,
  getCurriculum,
  getDocument,
  getGoal,
  getLlinkidGoalCorrelations,
  getMinimalCurricula,
} from './contentApiDataAccess';
import type {
  LoadCurriculumPayload,
  LoadDocumentPayload,
  LoadGoalsPayload,
} from './ContentApiStateTypes';
import { typedSelect } from '@utils/sagaUtils';

function* doInitCurriculaRoots() {
  try {
    const result = yield call(getMinimalCurricula);
    yield put(setCurriculaRoots(result));
  } catch (ex) {
    logAndCaptureException(ex);
    yield put(initCurriculaRootsFailed(ex.message));
    yield put(setAlert(contentApiLoadError));
  }
}

function* doInitCurriculaGoalLists() {
  try {
    const length: number = yield* typedSelect(
      (state: RootState) => state.contentApiData.curriculaGoalLists.length,
    );
    if (!length) {
      const result = yield call(getCurriculaGoalLists);
      yield put(setCurriculaGoalLists(result));
    }
  } catch (ex) {
    yield put(initCurriculaGoalListsFailed(ex.message));
    yield put(setAlert(contentApiLoadError));
  }
}

function* doInitCurriculaRelations() {
  try {
    const result = yield call(getCurriculaRelations);
    yield put(setCurriculaRelations(result));
  } catch (ex) {
    yield put(initCurriculaRelationsFailed(ex.message));
    yield put(setAlert(contentApiLoadError));
  }
}

function* loadDocumentSaga(action: PayloadAction<LoadDocumentPayload>) {
  const { key, refresh } = action.payload;
  try {
    const result = yield call(getDocument, key, refresh);
    yield put(setDocument({ key, document: result }));
  } catch (_ex) {
    yield put(loadDocumentFailed({ key }));
    yield put(setAlert(contentApiLoadError));
  }
}

function* loadCurriculumIntroduction(curriculum: { key: string }, refresh: boolean | undefined) {
  const gradeOr7th = yield* typedSelect((state: RootState) =>
    selectCurriculumGradeForIntroduction(state, { curriculumKey: curriculum.key }),
  );

  if (gradeOr7th) {
    const gradeKey = CONTENTAPI_DOCUMENT_KEYS.INTRODUCTION_GRADE[gradeOr7th];
    if (refresh !== undefined) {
      yield put(loadDocument({ key: gradeKey, refresh }));
    } else {
      yield put(loadDocument({ key: gradeKey }));
    }
  }
}

function* loadCurriculumView(action: PayloadAction<LoadCurriculumPayload>) {
  const { curriculumKey, refresh } = action.payload;
  try {
    const { isLoaded, isFailed } = yield* typedSelect(
      (state: RootState) => state.contentApiData.curriculaToLoad[curriculumKey],
    );
    if ((!isLoaded && !isFailed) || refresh) {
      const curriculum = yield call(getCurriculum, curriculumKey, refresh);
      yield put(setCurriculum({ curriculumKey, curriculum }));
      yield call(loadCurriculumIntroduction, curriculum, refresh);
    }
  } catch (ex) {
    console.error(ex);
    yield put(loadCurriculumFailed({ curriculumKey }));
    yield put(setAlert(contentApiLoadError));
  }
}

function* loadGoalView(key: string, refresh: boolean) {
  try {
    const { isLoaded, isFailed } = yield* typedSelect(
      (state: RootState) => state.contentApiData.goalsToLoad[key],
    );
    if ((!isLoaded && !isFailed) || refresh) {
      const goal = yield call(getGoal, key, refresh);
      yield put(setGoal({ key, goal }));
    }
  } catch (_ex) {
    yield put(loadGoalFailed({ key }));
  }
}

function* loadLlinkidGoalCorrelationSaga() {
  try {
    const curriculaCorrelation = yield call(getLlinkidGoalCorrelations);
    yield put(setGoalsCorrelations({ curriculaCorrelation }));
  } catch (_ex) {
    yield put(loadGoalsCorrelationsFailed());
  }
}

function* loadGoalViews(action: PayloadAction<LoadGoalsPayload>) {
  const { keys, refresh } = action.payload;
  yield all(keys.map((e) => fork(loadGoalView, e, refresh)));
}

export function* watchInitContentSaga() {
  yield takeLatest(initCurriculaRoots, doInitCurriculaRoots);
  yield takeLeading([initCurriculaGoalLists, setUrlParams], doInitCurriculaGoalLists);
  yield takeLatest(initCurriculaRelations, doInitCurriculaRelations);
}

export function* watchCurriculumSaga() {
  yield takeEvery(loadCurriculum, loadCurriculumView);
  yield takeEvery(loadDocument, loadDocumentSaga);
  yield takeEvery(loadGoals, loadGoalViews);
  yield takeEvery(loadGoalsCorrelations, loadLlinkidGoalCorrelationSaga);
}
